import restClient from 'Shared/hooks/restClient'
import useActionTranslate from "FlowBuilder/hooks/useActionTranslate";

import useLanguage from "FlowBuilder/hooks/useLanguageFlow";
import { channelConfig } from "./shareTypes";

function usePreviewShareContent() {
  const flow = useSel(s => s.taker?.flow || s.builder?.flow || {})
  const language = useLanguage()
  const { settings } = flow

  const variant = flow.shareContentTest?.contentVariants?.find(cv => cv.active !== false)

  if (!variant) return null

  const tc = variant.content?.textContent || {}
  const langContent = tc[language] || tc[flow.defaultLanguage] || tc['en'] || {}
  return {
    id: variant.id,
    title: langContent.shareTitle,
    description: langContent.shareDescription,
    imageUrl: langContent.shareImageUrl,
    shareText: langContent.shareReason
  }
}

export default function ShareForm({ action, onSubmit, publicUrl, onShare }) {
  const { config } = action
  const flowTakerId = useSel(s => s.taker?.flowTaker?.id)
  const isPreview = useSel(s => s.local.isPreview)
  const language = useLanguage()

  const { fallback: actionTextFallback } = useActionTranslate({ actionId: action.id })
  const [copied, setCopied] = useState(false)
  const [hasShared, setHasShared] = useState(false)
  const supportsNativeShare = typeof navigator !== 'undefined' && !!navigator.share

  const [shareData, setShareData] = useState(null)
  const previewContent = usePreviewShareContent()

  function getShareVersion() {
    restClient.get(`/api/target/share/variant`, {
      params: {
        flowId: action.flowId,
        actionId: action.id,
        flowTakerId,
        lang: language,
      }
    })
      .then(response => {
        if (response.data) {
          setShareData(response.data)
        }
      })
  }

  useEffect(() => {
    if (!isPreview) getShareVersion()
  }, [action.id, isPreview])

  useEffect(() => {
    let timeoutId

    if (copied) {
      timeoutId = setTimeout(() => {
        setCopied(false)
      }, 3000)
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [copied])

  const firstVariant = isPreview
    ? previewContent
    : shareData?.channels?.[0]?.variant

  const sharedUrl = useMemo(() => {
    const tid = isPreview ? 'preview' : shareData?.tid
    if (tid && firstVariant?.id) {
      try {
        const url = new URL(publicUrl)
        url.pathname += '/shared'
        url.searchParams.set('t', tid)
        url.searchParams.set('v', String(firstVariant.id))
        return url.toString()
      } catch (e) {
        return publicUrl
      }
    }
    return publicUrl
  }, [publicUrl, isPreview, shareData?.tid, firstVariant?.id])

  if (!firstVariant) return (
    <div className="share-preview">
      <span className="skeleton-box" style={{ width: '30%', height: 'auto', aspectRatio: '16/9', marginRight: '8px' }}></span>
      <div className="share-info">
        <div className="skeleton-box" style={{ width: '60%', height: '1.2em' }}></div>
        <div className="skeleton-box" style={{ width: '80%', height: '0.8em', marginTop: '8px' }}></div>
      </div>
    </div>
  )

  const { title: shareTitle, description: shareDescription, imageUrl: shareImageUrl } = firstVariant

  const handleCopy = async () => {
    onShare('link')
    await navigator.clipboard.writeText(sharedUrl)
    setCopied(true)
    setHasShared(true)
  }

  const shareAndRedirect = async (channel) => {
    if (channel === 'native') {
      try {
        await navigator.share({
          title: shareTitle,
          text: firstVariant.shareText || shareDescription,
          url: sharedUrl,
        })
      } catch (err) {
        if (err.name === 'AbortError') return
        throw err
      }
    } else {
      const getUrl = channelConfig[channel]['getUrl']
      window.open(getUrl(sharedUrl, firstVariant.shareText), '_blank')
    }

    onShare(channel)
    setHasShared(true)
  }

  return <>
    {shareImageUrl && (
    <div className="share-preview">
      <img src={shareImageUrl} />
      <div className="share-info">
        <h4 className="no-margin-top">{shareTitle}</h4>
        <p>{shareDescription}</p>
      </div>
    </div>)}
    <div className="share-form">
      <div className="share-button-container">
        {(config?.channels || []).filter((channel) => {
          if (channel === 'native' && !supportsNativeShare) return false
          return true
        }).map((channel, index) => {
          const { translateKey, svg, primaryBackground = () => { } } = channelConfig[channel] || {}

          return (
            <div
              key={channel}
              className={`add-padding ${index === 0 ? 'primary-share-link' : 'secondary-share-link'}`}
              style={index === 0 ? { backgroundColor: primaryBackground } : {}}
              onClick={() => shareAndRedirect(channel)}
            >
              <span className={`share-button flex gap-12 align-center`}>{svg}{actionTextFallback[translateKey]}</span>
            </div>
          )
        })}
      </div>

      <div className="margin-top share-link-container">
        <h4 className="larger">{actionTextFallback.shareLinkText}</h4>
        <div className="share-box flex align-center gap-8">
          <input value={sharedUrl} onClick={handleCopy} readOnly />
          <SvgIconsCopy onClick={handleCopy} className="copy-icon" />
        </div>
        <p className="smaller lighter">{copied ? actionTextFallback.copied : actionTextFallback.copyLinkToShare}</p>
        <div className="flex justify-center">
          <a className='skip-button' onClick={onSubmit}>
            {hasShared ? actionTextFallback.continueOnceShared : actionTextFallback.skipButtonText}
          </a>
        </div>
      </div>
    </div>
  </>
}
